import { useState, type FormEvent } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Building2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { logger } from '../../lib/logger';

export function OrganizationSetup() {
  const { createOrganization } = useOrganization();
  const [name, setName] = useState('');
  const [type, setType] = useState('Partido Político');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Auto-generate slug from name
      const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

      await createOrganization(name, slug, type);
      // Organization context will update and app will show main interface
    } catch (err: any) {
      logger.error('[OrganizationSetup] Error creating organization:', err);
      setError(err.message || 'Error al crear la organización. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5D1828] via-[#7A2235] to-[#8B2E3F] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#5D1828] to-[#7A2235] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#111827] mb-2">
            Crear organización
          </h1>
          <p className="text-[#6B7280]">
            Configura tu organización para comenzar
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 text-sm"
          >
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-2">
              Nombre de la organización
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="Ej: Partido Acción Nacional"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-2">
              Tipo de organización
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="Partido Político">Partido Político</option>
              <option value="Gobierno">Gobierno</option>
              <option value="Campaña">Campaña</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#5D1828]/30 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creando organización...
              </>
            ) : (
              <>
                <Building2 size={20} />
                Crear organización
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
